import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  inject,
  DestroyRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';

import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { of, map, switchMap } from 'rxjs';

import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import UrlUtils from 'app/shared/Utils/url.utils';
import { PipesModule } from 'app/shared/pipes/pipes.module';
import { Property } from 'app/modules/projects/projects.types';
import { ResearchService } from 'app/modules/research/research.service';

declare interface DialogData {
  property: any;
  project: any;
  data: any;
}

@Component({
  templateUrl: './demographics.component.html',
  styleUrls: ['./demographics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTabsModule,
    MatOptionModule,
    MatSelectModule,
    MatProgressBarModule,
    PipesModule,
    NgApexchartsModule,
  ],
})
export class DemographicsDialogComponent implements OnInit {
  project: any;
  data: any;
  property: Property;
  parcelData: any;
  postalCode = null;
  public aggregateMarketDemographics = null;
  blsUnemploymentOptions: ApexOptions;
  blsUnemploymentData: any;
  censusPermitsOptions: ApexOptions;
  censusPermitsData: any;
  demographicDistanceType: string = 'RingBuffer';
  marketDataDistanceType: string = 'MILES';
  demographicDistanceRadius: any = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    RingBuffer: [1, 3, 5],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    DriveTimeBuffer: [10, 20, 30],
  };
  demographicsLoading: boolean = false;

  private latitude = null;
  private longitude = null;
  private apn = null;
  private schoolRatings: any[] = [];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _researchService: ResearchService,
    private readonly matDialogRef: MatDialogRef<DemographicsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dailogData: DialogData,
  ) {}

  ngOnInit() {
    if (this.dailogData) {
      this.initDialogData();
    }

    const driveTimeType = UrlUtils.getFromLocalStorage('demographicDistanceType', false);

    if (driveTimeType) {
      this.demographicDistanceType = driveTimeType;
    }

    if (this.property) {
      if (this.property.address && this.property.address.latitude && this.property.address.longitude) {
        this.postalCode = this.property.address?.postal_code;
        this.latitude = this.property.address.latitude;
        this.longitude = this.property.address.longitude;
      }
      if (this.property.remote_id) {
        this.apn = this.property.apn;
      }
    } else if (this.project.postal_code) {
      this.postalCode = this.project.postal_code;
    } else if (this.project && this.project.properties && this.project.properties.length > 0) {
      for (let property of this.project.properties) {
        if (property.address && property.address.latitude && property.address.longitude) {
          this.postalCode = property.address?.postal_code;
          this.latitude = property.address.latitude;
          this.longitude = property.address.longitude;
          break;
        }
      }
    }

    if (this.latitude && this.longitude) {
      this.demographicsLoading = true;

      this._researchService
        .getAllAggregateMarketDemographics(this.latitude, this.longitude, 'BLOCK_GROUP', this.marketDataDistanceType)
        .subscribe((data) => {
          this.aggregateMarketDemographics = data;
          this.demographicsLoading = false;
          this._changeDetectorRef.markForCheck();
        });
    }

    if (this.apn && this.latitude && this.longitude) {
      this._researchService
        .getParcelByApn(this.apn, this.latitude, this.longitude)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap((parcelData: any) => {
            if (parcelData) {
              this.parcelData = parcelData;
              if (!this.parcelData?.usa_owner_unmask__tax_assessor_id) {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                this.parcelData = { ...this.parcelData, usa_owner_unmask__tax_assessor_id: [] };
              }
              return this._researchService
                .getSchoolRatings(this.latitude, this.longitude)
                .pipe(map((schoolRatings) => ({ parcelData, schoolRatings })));
            }
            return of(null);
          }),
        )
        .subscribe((result) => {
          if (result) {
            const { parcelData, schoolRatings } = result;
            if (parcelData.tax_assessor_usa_school__bridge) {
              for (let school of parcelData.tax_assessor_usa_school__bridge) {
                if (school.usa_school__institution_id) {
                  let name = school.usa_school__institution_id.geography_name;
                  let rating = this.getSchoolRating(schoolRatings, name);
                  let schoolRating = {
                    name: name,
                    type: school.usa_school__institution_id.is_elementary_school
                      ? 'Elementary School'
                      : school.usa_school__institution_id.is_high_school
                        ? 'High School'
                        : 'Middle School',
                    district: school.usa_school__institution_id.district_name,
                    educationalClimate: school.usa_school__institution_id.educational_climate_index,
                    rating: rating,
                  };
                  this.schoolRatings.push(schoolRating);
                }
              }
            }
            this._changeDetectorRef.markForCheck();
          }
        });
    }
  }

  public onChangeDemographicDistancType(): void {
    UrlUtils.saveToLocalStorage('marketDataDistanceType', this.marketDataDistanceType);

    this._researchService
      .getAllAggregateMarketDemographics(this.latitude, this.longitude, 'BLOCK_GROUP', this.marketDataDistanceType)
      .subscribe((data) => {
        this.aggregateMarketDemographics = data;
        this._changeDetectorRef.markForCheck();
      });
  }

  private getSchoolRating(schoolRatings, schoolName) {
    if (schoolRatings && schoolRatings.length > 0) {
      const name = schoolName?.replace('SCHOOL', '').trim();
      let index = schoolRatings.findIndex((item) => item.name.toUpperCase() == schoolName);

      if (index > -1) {
        let schoolRating = schoolRatings[index];
        return schoolRating.rating;
      } else {
        index = schoolRatings.findIndex((item) => item.name.toUpperCase() == name);
        if (index > -1) {
          let schoolRating = schoolRatings[index];
          return schoolRating.rating;
        } else {
          return null;
        }
      }
    }
    return null;
  }

  private initDialogData(): void {
    this.data = this.dailogData.data;
    this.project = this.dailogData.project;
    this.property = this.dailogData.property;
  }

  discard(): void {
    this.matDialogRef.close();
  }
}
